import { Skeleton } from '@shared/components/Skeleton';
import { useTranslation } from '@shared/core';
import { useTextLineClampOverflow } from '@shared/hooks/useTextLineClampOverflow';
import { useCurrencyContext } from '@shared/utils/currency';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Box, BoxProps, Flex, TextV2, Divider as JKDivider, styled } from '@withjoy/joykit';
import { Diamond, MapMarker } from '@withjoy/joykit/icons';
import React, { useState } from 'react';

interface AccommodationOfferingCardProps {
  children: React.ReactNode;
  marginTop?: BoxProps['marginTop'];
}

const AccommodationOfferingCard = ({ children, marginTop }: AccommodationOfferingCardProps) => {
  return (
    <Box borderRadius={4} boxShadow="0px 4px 16px 0px rgba(0, 0, 0, 0.07), 0px 7px 27px 0px rgba(44, 41, 37, 0.06)" padding={5} marginTop={marginTop}>
      {children}
    </Box>
  );
};

const AssetContainer: React.FC = ({ children }) => {
  return (
    <Box borderRadius={3} position="relative">
      {children}
    </Box>
  );
};

const Asset = ({ imageUrl }: { imageUrl: string }) => {
  return <Box as="img" src={imageUrl} title="Lodging Offering" objectFit="cover" aspectRatio={'16 / 9'} width="100%" borderRadius="inherit" />;
};

const AssetBadge = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      backgroundColor="rgba(255, 38, 23, 1)"
      paddingY={1}
      paddingX={3}
      borderRadius="full"
      position="absolute"
      top={'10px'}
      right="10px"
      typographyVariant="label3"
      color="white"
    >
      <span role="img" aria-label="Going fast">
        ️⚡️
      </span>{' '}
      {children}
    </Box>
  );
};

const TitleSection = ({
  title,
  isPreferredOffering,
  marginTop,
  distanceFromVenue
}: {
  marginTop: BoxProps['marginTop'];
  title: string;
  isPreferredOffering: boolean;
  distanceFromVenue?: {
    value: number | string;
    format: 'mi' | 'km';
  };
}) => {
  const { t } = useTranslation('thanksForRSVP');
  const trans = t('accommodationsPushStep', 'offeringCard', 'distanceFromVenue');
  return (
    <Flex flexDirection="column" marginTop={marginTop} justifyContent="flex-start">
      <Flex columnGap={6} alignItems="center" width="100%">
        <TextV2 typographyVariant="hed2" flex={1} maxWidth="95%">
          {title}
        </TextV2>
        {isPreferredOffering && <Diamond size={32} color="brandYellow4" />}
      </Flex>
      {distanceFromVenue && (
        <TextV2 display="inline-flex" alignItems="center" columnGap={1} typographyVariant="label2" color="mono8" marginTop={3}>
          <MapMarker size={16} /> {trans[distanceFromVenue.format]?.({ distance: distanceFromVenue.value })}
        </TextV2>
      )}
    </Flex>
  );
};

type RateType = 'daily';

const RateSection = ({
  rateInMinorUnits,
  marginTop,
  rateType,
  showSkeleton,
  currency,
  rateDisplayOverride
}: {
  marginTop?: BoxProps['marginTop'];
  rateType: RateType;
  rateInMinorUnits: number;
  rateDisplayOverride?: string;
  showSkeleton?: boolean;
  currency?: string;
}) => {
  const currencyUtil = useCurrencyContext();
  const { t } = useTranslation('thanksForRSVP');
  const trans = t('accommodationsPushStep', 'offeringCard', 'rateSection');

  return (
    <Flex columnGap={6} alignItems="center" marginTop={marginTop}>
      <Box flex={1}>
        <Box typographyVariant="hed1" color="mono14">
          {trans.title()}
        </Box>
        <Box typographyVariant="label2" color="mono10">
          {trans.rateType[rateType]?.()}
        </Box>
      </Box>

      <Box>
        <Skeleton isReady={!showSkeleton} placeholder={<Skeleton width={80} height={36} />}>
          <TextV2 typographyVariant={rateDisplayOverride ? 'hed5' : 'display1'}>
            {rateDisplayOverride || currencyUtil.formatCurrency(rateInMinorUnits / 100, { form: 'short', precision: 0, currency })}
          </TextV2>
        </Skeleton>
      </Box>
    </Flex>
  );
};

const RoomBlockRateSection = ({
  cutoffDate,
  strikeoutPricePerNightString,
  pricePerNightString
}: {
  cutoffDate: Maybe<string>;
  strikeoutPricePerNightString: string | undefined;
  pricePerNightString: string | undefined;
}) => {
  const { t } = useTranslation('thanksForRSVP');

  const roomBlockTrans = t('accommodationsPushStep', 'offeringCard', 'roomBlockRateSection');
  return (
    <Flex justifyContent={cutoffDate ? 'space-between' : 'flex-end'}>
      {cutoffDate && (
        <Flex flexDirection="column" rowGap={1}>
          <TextV2 color="mono10" typographyVariant="body1">
            {roomBlockTrans.bookBefore()}
          </TextV2>
          <TextV2 color="mono14" typographyVariant="body1">
            {cutoffDate}
          </TextV2>
        </Flex>
      )}
      <Flex flexDirection="column" rowGap={1} textAlign="right">
        <TextV2 color="mono10" typographyVariant="body1">
          {roomBlockTrans.groupRate()}
        </TextV2>
        <Flex columnGap={3} alignItems="center">
          {strikeoutPricePerNightString && (
            <TextV2
              color="mono8"
              typographyVariant="body3"
              backgroundSize={'100% 1.5px'}
              backgroundImage={'linear-gradient(currentColor, currentColor)'}
              backgroundPosition={'0% 50%'}
              backgroundRepeat={'no-repeat'}
            >
              {strikeoutPricePerNightString}
            </TextV2>
          )}

          <TextV2 color="mono14" typographyVariant="hed2">
            {roomBlockTrans.pricePerNight({ price: pricePerNightString })}
          </TextV2>
        </Flex>
      </Flex>
    </Flex>
  );
};

const TextEllipsisWrapper = styled(Box)<{ lineClamp?: number | string }>`
  display: -webkit-box;
  -webkit-line-clamp: ${props => props.lineClamp || 1};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  white-space: break-spaces;
`;

const Note = ({ note }: { note: string }) => {
  const { textRef, isOverflowing } = useTextLineClampOverflow(3);
  const [isShowingMoreDescription, setShowMoreDescription] = useState(false);

  const handleOnToggleNote = useEventCallback<React.MouseEventHandler>(e => {
    e.preventDefault();
    e.stopPropagation();
    setShowMoreDescription(prev => !prev);
  });

  return (
    <Flex flexDirection="column">
      <TextEllipsisWrapper ref={textRef} lineClamp={isShowingMoreDescription ? 'none' : 3} lineHeight={1.5}>
        <TextV2 typographyVariant="body1" fontWeight={400} color="mono12" fontStyle="italic" title={note} lineHeight={1.5}>
          {note}
        </TextV2>
      </TextEllipsisWrapper>

      {isOverflowing && (
        <TextV2 typographyVariant="body1" fontWeight={600} color="mono14" onClick={handleOnToggleNote} cursor="pointer">
          {isShowingMoreDescription ? 'Less' : 'More'}
        </TextV2>
      )}
    </Flex>
  );
};

const Divider = () => {
  return <JKDivider orientation="horizontal" marginY={5} />;
};

AccommodationOfferingCard.AssetContainer = AssetContainer;
AccommodationOfferingCard.Asset = Asset;
AccommodationOfferingCard.AssetBadge = AssetBadge;
AccommodationOfferingCard.TitleSection = TitleSection;
AccommodationOfferingCard.RateSection = RateSection;
AccommodationOfferingCard.RoomBlockRateSection = RoomBlockRateSection;
AccommodationOfferingCard.Divider = Divider;
AccommodationOfferingCard.Note = Note;

export { AccommodationOfferingCard };
